import { useEffect, useRef, useState } from 'react';
import ComposeEmail from '@/components/email/ComposeEmail';
import { useEmail } from '@/context/EmailContext';
import { Email } from '@/context/email/types';

const splitAddresses = (value: string): string[] =>
  value.split(',').map((s) => s.trim()).filter(Boolean);

/**
 * App-wide compose window. Mounted once in AppLayout; any feature opens it
 * via useComposeEmail().openCompose({ to, subject, bodyHtml, ... }).
 * Remounts on each open so the prefill lands in a fresh editor.
 */
const GlobalComposeOverlay = () => {
  const { composeOpen, composeDraft, setComposeOpen, resetComposeDraft } = useEmail();

  // Remount the composer on every open so initialEmail state re-initializes.
  const [mountKey, setMountKey] = useState(0);
  const prevOpenRef = useRef(false);
  useEffect(() => {
    if (composeOpen && !prevOpenRef.current) {
      setMountKey((k) => k + 1);
    }
    prevOpenRef.current = composeOpen;
  }, [composeOpen]);

  if (!composeOpen) return null;

  const initialEmail: Partial<Email> = {
    to: splitAddresses(composeDraft.to),
    cc: splitAddresses(composeDraft.cc),
    bcc: splitAddresses(composeDraft.bcc),
    subject: composeDraft.subject,
    body: composeDraft.body,
    body_html: composeDraft.bodyHtml || '',
  };

  const handleClose = () => {
    setComposeOpen(false);
    resetComposeDraft?.();
  };

  return (
    <ComposeEmail
      key={mountKey}
      initialEmail={initialEmail}
      leadId={composeDraft.leadId}
      contactId={composeDraft.contactId}
      onClose={handleClose}
      onMinimize={handleClose}
      onSent={handleClose}
    />
  );
};

export default GlobalComposeOverlay;
